import { LogOut } from "lucide-react";
import paduLogo from "@/assets/padu_new_logo_sign.png";
import { CONTENT_PAGES, HOME_PAGES, LEGAL_PAGES } from "./types";
import type { ActiveView } from "./types";

interface AdminSidebarProps {
  activeView: ActiveView;
  onNavigate: (view: ActiveView) => void;
  onLogout: () => void;
}

function NavButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
        active
          ? "bg-[#f0f0f0] text-gray-900 font-medium"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}

export function AdminSidebar({ activeView, onNavigate, onLogout }: AdminSidebarProps) {
  return (
    <aside className="w-56 h-full bg-white border-r border-gray-200 flex flex-col shrink-0">
      <div className="flex items-center gap-2 px-5 py-5 border-b border-gray-100">
        <div className="w-7 h-7 rounded-md flex items-center justify-center">
          <img src={paduLogo} alt="PADU" className="w-full h-full object-contain" />
        </div>
        <span className="font-semibold text-sm text-gray-800">PADU Admin</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Contact</p>
          <NavButton
            active={activeView.type === "messages"}
            onClick={() => onNavigate({ type: "messages" })}
          >
            Messages
          </NavButton>
          <NavButton
            active={activeView.type === "leads"}
            onClick={() => onNavigate({ type: "leads" })}
          >
            Leads
          </NavButton>
          <NavButton
            active={activeView.type === "questionnaires"}
            onClick={() => onNavigate({ type: "questionnaires" })}
          >
            Questionnaires
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Redirects</p>
          <NavButton
            active={activeView.type === "redirects"}
            onClick={() => onNavigate({ type: "redirects" })}
          >
            Redirects
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Partners</p>
          <NavButton
            active={activeView.type === "partners"}
            onClick={() => onNavigate({ type: "partners" })}
          >
            Partners
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Media</p>
          <NavButton
            active={activeView.type === "videos"}
            onClick={() => onNavigate({ type: "videos" })}
          >
            Page Videos
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Site Content</p>
          <ul className="space-y-0.5">
            {CONTENT_PAGES.map((page) => (
              <li key={page.slug}>
                <NavButton
                  active={activeView.type === "content" && activeView.slug === page.slug}
                  onClick={() => onNavigate({ type: "content", ...page })}
                >
                  {page.label}
                </NavButton>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">News</p>
          <NavButton
            active={activeView.type === "news"}
            onClick={() => onNavigate({ type: "news" })}
          >
            Manage News
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Landing Pages</p>
          <NavButton
            active={activeView.type === "landing-pages" || activeView.type === "landing-page-editor"}
            onClick={() => onNavigate({ type: "landing-pages" })}
          >
            Manage Landing Pages
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Home Subpages</p>
          <ul className="space-y-0.5">
            {HOME_PAGES.map((page) => (
              <li key={page.slug}>
                <NavButton
                  active={activeView.type === "content" && activeView.slug === page.slug}
                  onClick={() => onNavigate({ type: "content", ...page })}
                >
                  {page.label}
                </NavButton>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Nova</p>
          <NavButton
            active={activeView.type === "nova-header"}
            onClick={() => onNavigate({ type: "nova-header" })}
          >
            Header
          </NavButton>
          <NavButton
            active={activeView.type === "nova-featured-offer"}
            onClick={() => onNavigate({ type: "nova-featured-offer" })}
          >
            Featured Offer
          </NavButton>
          <NavButton
            active={activeView.type === "nova-deals"}
            onClick={() => onNavigate({ type: "nova-deals" })}
          >
            Nova Deals
          </NavButton>
          <NavButton
            active={activeView.type === "nova-perks"}
            onClick={() => onNavigate({ type: "nova-perks" })}
          >
            Perks
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Settings</p>
          <NavButton
            active={activeView.type === "email-settings" && activeView.kind === "messages"}
            onClick={() => onNavigate({ type: "email-settings", kind: "messages" })}
          >
            Messages
          </NavButton>
          <NavButton
            active={activeView.type === "email-settings" && activeView.kind === "leads"}
            onClick={() => onNavigate({ type: "email-settings", kind: "leads" })}
          >
            Leads
          </NavButton>
        </div>

        <div>
          <p className="text-[11px] font-bold text-gray-900 uppercase tracking-widest px-2 mb-1">Legal</p>
          <ul className="space-y-0.5">
            {LEGAL_PAGES.map((page) => (
              <li key={page.slug}>
                <NavButton
                  active={activeView.type === "content" && activeView.slug === page.slug}
                  onClick={() => onNavigate({ type: "content", ...page })}
                >
                  {page.label}
                </NavButton>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <div className="px-3 py-3 border-t border-gray-100">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <LogOut size={14} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
